import React, { useRef, useState } from "react";
import { PiSmileyFill } from "react-icons/pi";
import { HiOutlinePencilAlt } from "react-icons/hi";

import { createPost } from "../../api";
import ToastMessage from "../ToastMessage";
import PostButton from "../PostButton";
import EmojiReactPicker from "../EmojiReactPicker";

const Text = ({ close }) => {
  const [showEmoji, setShowEmoji] = useState(false);
  const [text, setText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const smileyRef = useRef(null);

  const type = "text";
  const maxLength = 1000;
  const characterCount = text.length;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!text.trim()) {
      ToastMessage("Please write something before posting.");
      return;
    }

    if (isSubmitting) return;

    const formData = new FormData();

    formData.append("type", type);
    formData.append("content", text.trim());

    try {
      setIsSubmitting(true);

      const res = await createPost(formData);

      if (res?.data?.message) {
        setText("");
        setShowEmoji(false);

        ToastMessage(res.data.message);

        close?.();
      }
    } catch (err) {
      console.error(err);
      ToastMessage("Error creating post.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white transition-all duration-200 focus-within:border-purple-300 focus-within:ring-4 focus-within:ring-purple-50">
        {/* ================= COMPOSER HEADER ================= */}

        <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-50 text-purple-600">
            <HiOutlinePencilAlt size={18} />
          </div>

          <div>
            <p className="text-sm font-semibold text-gray-900">Text post</p>
            <p className="mt-0.5 text-[11px] text-gray-400">Share what&apos;s on your mind</p>
          </div>
        </div>

        {/* ================= TEXTAREA ================= */}

        <div className="relative">
          <label htmlFor="post-content" className="sr-only">Write your post</label>

          <textarea
            id="post-content"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={9}
            maxLength={maxLength}
            placeholder="What's on your mind?"
            className="min-h-[220px] w-full resize-none border-none bg-white px-4 py-4 pr-12 text-[15px] font-normal leading-7 text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-0"
          />

          {/* Emoji Button */}

          <button
            ref={smileyRef}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setShowEmoji((prev) => !prev);
            }}
            className={`absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center rounded-xl transition-all duration-200 ${showEmoji ? "bg-purple-50 text-purple-600" : "bg-gray-50 text-gray-400 hover:bg-purple-50 hover:text-purple-600"}`}
          >
            <PiSmileyFill size={20} />
          </button>

          {/* Emoji Picker */}

          <EmojiReactPicker set={setText} showEmoji={showEmoji} setShowEmoji={setShowEmoji} triggerRef={smileyRef} />
        </div>

        {/* ================= BOTTOM TOOLBAR ================= */}

        <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50/50 px-4 py-2.5">
          <p className="text-xs text-gray-400">Express yourself freely</p>

          <span className={`text-[11px] font-medium ${characterCount > maxLength * 0.9 ? "text-orange-500" : "text-gray-400"}`}>
            {characterCount}/{maxLength}
          </span>
        </div>
      </div>

      {/* ================= POST ACTION ================= */}

      <div className="mt-4 flex items-center justify-end">
        <div className={`transition-all duration-200 ${text.trim() ? "opacity-100" : "pointer-events-none opacity-50"}`}>
          <PostButton type="submit" disabled={!text.trim() || isSubmitting} />
        </div>
      </div>
    </form>
  );
};

export default Text;